import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { CommonRouteQueryComposable } from '@/composables/routeQueries/useCommonRouteQuery';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';

import { useLoaderStore } from '@/stores/useLoaderStore';
import {
  fetchGetDashboardCustomer,
  fetchGetJobAirroad,
  fetchGetJobDetailedOrGetJobDetailedByRhId,
  fetchGetJobLocation,
  fetchGetJobStopImage,
  fetchGetJobTicketsSearchByJob,
  fetchGetZammadChannelsEmails,
} from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

const dataRoutes = [
  routeNames.DETAILED_PARAM_JOB,
  routeNames.DETAILED_QUERY_JOB,
];

export interface DetailedJobPageViewDataComposable {
  isFetchingDetailedJob: Readonly<Ref<boolean>>;
  isFetchingDetailedCustomer: Readonly<Ref<boolean>>;
  isFetchingShortZammadTickets: Readonly<Ref<boolean>>;
  isFetchingAirroadJobs: Readonly<Ref<boolean>>;
  isFetchingStopImages: Readonly<Ref<boolean>>;
  isFetchingChannelsEmails: Readonly<Ref<boolean>>;
  isFetchingStopLocations: Readonly<Ref<boolean>>;

  detailedJob: Readonly<
    Ref<ReadonlyDeep<openapi.JobDetailedOpenapi> | undefined>
  >;
  detailedCustomer: Readonly<
    Ref<ReadonlyDeep<openapi.CustomerDetailContentOpenapi> | undefined>
  >;
  shortZammadTickets: Readonly<
    Ref<ReadonlyDeep<openapi.JobGetTicketsByJobShortContentOpenapi> | undefined>
  >;
  airroadJobs: Readonly<
    Ref<ReadonlyDeep<openapi.JobGetAirroadContentOpenapi> | undefined>
  >;
  stopImages: Readonly<
    Ref<ReadonlyDeep<openapi.GetStopImagesContentOpenapi> | undefined>
  >;
  channelsEmails: Readonly<
    Ref<ReadonlyDeep<openapi.ZammadChannelsEmailsContentOpenapi> | undefined>
  >;
  stopLocations: Readonly<
    Ref<ReadonlyDeep<openapi.JobLocationsContentOpenapi> | undefined>
  >;

  detailedJobError: Readonly<Ref<unknown | undefined>>;
  detailedCustomerError: Readonly<Ref<unknown | undefined>>;
  shortZammadTicketsError: Readonly<Ref<unknown | undefined>>;
  airroadJobsError: Readonly<Ref<unknown | undefined>>;
  stopImagesError: Readonly<Ref<unknown | undefined>>;
  channelsEmailsError: Readonly<Ref<unknown | undefined>>;
  stopLocationsError: Readonly<Ref<unknown | undefined>>;

  refreshDetailedJob: () => Promise<void>;
  refreshDetailedCustomer: () => Promise<void>;
  refreshShortZammadTickets: () => Promise<void>;
  refreshAirroadJobs: () => Promise<void>;
  refreshStopImages: () => Promise<void>;
  refreshChannelsEmails: () => Promise<void>;
  refreshStopLocations: () => Promise<void>;

  reload: () => DetailedJobPageViewDataComposable;
}

export default function useDetailedJobPageViewData(
  routeQuery: CommonRouteQueryComposable,
): DetailedJobPageViewDataComposable {
  const route = useRoute();
  const { queryJobId, queryJobDate } = routeQuery;

  const loaderStore = useLoaderStore();

  let detailedJobController: AbortController | undefined;
  let detailedCustomerController: AbortController | undefined;
  let shortZammadTicketsController: AbortController | undefined;
  let airroadJobsController: AbortController | undefined;
  let stopImagesController: AbortController | undefined;
  let channelsEmailsController: AbortController | undefined;
  let stopLocationsController: AbortController | undefined;

  const detailedJobFetchCounter = ref(0);
  const detailedCustomerFetchCounter = ref(0);
  const shortZammadTicketsFetchCounter = ref(0);
  const airroadJobsFetchCounter = ref(0);
  const stopImagesFetchCounter = ref(0);
  const channelsEmailsFetchCounter = ref(0);
  const stopLocationsFetchCounter = ref(0);

  const isFetchingDetailedJob = computed(() => !!detailedJobFetchCounter.value);
  const isFetchingDetailedCustomer = computed(
    () => !!detailedCustomerFetchCounter.value,
  );
  const isFetchingShortZammadTickets = computed(
    () => !!shortZammadTicketsFetchCounter.value,
  );
  const isFetchingAirroadJobs = computed(() => !!airroadJobsFetchCounter.value);
  const isFetchingStopImages = computed(() => !!stopImagesFetchCounter.value);
  const isFetchingChannelsEmails = computed(
    () => !!channelsEmailsFetchCounter.value,
  );
  const isFetchingStopLocations = computed(
    () => !!stopLocationsFetchCounter.value,
  );

  const detailedJob = ref<ReadonlyDeep<openapi.JobDetailedOpenapi>>();
  const detailedCustomer =
    ref<ReadonlyDeep<openapi.CustomerDetailContentOpenapi>>();
  const shortZammadTickets =
    ref<ReadonlyDeep<openapi.JobGetTicketsByJobShortContentOpenapi>>();
  const airroadJobs = ref<ReadonlyDeep<openapi.JobGetAirroadContentOpenapi>>();
  const stopImages = ref<ReadonlyDeep<openapi.GetStopImagesContentOpenapi>>();
  const channelsEmails =
    ref<ReadonlyDeep<openapi.ZammadChannelsEmailsContentOpenapi>>();
  const stopLocations = ref<ReadonlyDeep<openapi.JobLocationsContentOpenapi>>();

  const detailedJobError = ref();
  const detailedCustomerError = ref();
  const shortZammadTicketsError = ref();
  const airroadJobsError = ref();
  const stopImagesError = ref();
  const channelsEmailsError = ref();
  const stopLocationsError = ref();

  function clear(): void {
    if (detailedJobController) detailedJobController.abort();
    if (detailedCustomerController) detailedCustomerController.abort();
    if (shortZammadTicketsController) shortZammadTicketsController.abort();
    if (airroadJobsController) airroadJobsController.abort();
    if (stopImagesController) stopImagesController.abort();
    if (channelsEmailsController) channelsEmailsController.abort();
    if (stopLocationsController) stopLocationsController.abort();
  }

  onUnmounted(() => {
    clear();
  });

  async function _fetchDetailedJob(): Promise<
    ReadonlyDeep<openapi.JobDetailedOpenapi> | undefined
  > {
    if (detailedJobController) detailedJobController.abort();
    const jobId = queryJobId.value;
    const jobDate = queryJobDate.value;
    if (!jobId) return;
    detailedJobController = new AbortController();
    return await fetchGetJobDetailedOrGetJobDetailedByRhId(
      { jobId, jobDate },
      {
        signal: detailedJobController.signal,
      },
    )
      .then((data) => {
        detailedJobError.value = undefined;
        return data;
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return undefined;
        detailedJobError.value = err;
        throw err;
      });
  }

  async function _fetchDetailedCustomer(): Promise<
    ReadonlyDeep<openapi.CustomerDetailContentOpenapi> | undefined
  > {
    if (detailedCustomerController) detailedCustomerController.abort();
    if (!detailedJob.value) return;
    const customerCode = detailedJob.value.customer;
    if (!customerCode) return;
    detailedCustomerController = new AbortController();
    return await fetchGetDashboardCustomer(
      {
        code: customerCode,
      },
      {
        signal: detailedCustomerController.signal,
      },
    )
      .then((data) => {
        detailedCustomerError.value = undefined;
        return data;
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return undefined;
        detailedCustomerError.value = err;
        throw err;
      });
  }

  async function _fetchShortZammadTickets(): Promise<
    ReadonlyDeep<openapi.JobGetTicketsByJobShortContentOpenapi> | undefined
  > {
    if (shortZammadTicketsController) shortZammadTicketsController.abort();
    if (!detailedJob.value) return;
    const jobId = detailedJob.value.rockhopper_id;
    const jobDate = detailedJob.value.rockhopper_date;
    if (!jobId || !jobDate) return;
    shortZammadTicketsController = new AbortController();
    return await fetchGetJobTicketsSearchByJob(
      {
        job_id: jobId,
        job_date: jobDate,
      },
      {
        signal: shortZammadTicketsController.signal,
      },
    )
      .then((data) => {
        shortZammadTicketsError.value = undefined;
        return data;
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return undefined;
        shortZammadTicketsError.value = err;
        throw err;
      });
  }

  async function _fetchAirroadJobs(): Promise<
    ReadonlyDeep<openapi.JobGetAirroadContentOpenapi> | undefined
  > {
    if (airroadJobsController) airroadJobsController.abort();
    if (!detailedJob.value) return;
    const jobId = detailedJob.value.rockhopper_id;
    const jobDate = detailedJob.value.rockhopper_date;
    if (!jobId || !jobDate) return;
    airroadJobsController = new AbortController();
    return await fetchGetJobAirroad(
      {
        job_id: jobId,
        job_date: jobDate,
      },
      {
        signal: airroadJobsController.signal,
      },
    )
      .then((data) => {
        airroadJobsError.value = undefined;
        return data;
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return undefined;
        airroadJobsError.value = err;
        throw err;
      });
  }

  async function _fetchStopImages(): Promise<
    ReadonlyDeep<openapi.GetStopImagesContentOpenapi> | undefined
  > {
    if (stopImagesController) stopImagesController.abort();
    if (!detailedJob.value) return;
    if (!detailedJob.value.has_stop_images) return;
    const jobId = detailedJob.value.rockhopper_id;
    const jobDate = detailedJob.value.rockhopper_date;
    if (!jobId || !jobDate) return;
    stopImagesController = new AbortController();
    return await fetchGetJobStopImage(
      {
        job_id: jobId,
        job_date: jobDate,
      },
      {
        signal: stopImagesController.signal,
      },
    )
      .then((data) => {
        stopImagesError.value = undefined;
        return data;
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return undefined;
        stopImagesError.value = err;
        throw err;
      });
  }

  async function _fetchChannelsEmails(): Promise<
    ReadonlyDeep<openapi.ZammadChannelsEmailsContentOpenapi> | undefined
  > {
    if (channelsEmailsController) channelsEmailsController.abort();
    channelsEmailsController = new AbortController();
    return await fetchGetZammadChannelsEmails({
      signal: channelsEmailsController.signal,
    })
      .then((data) => {
        channelsEmailsError.value = undefined;
        return data;
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return undefined;
        channelsEmailsError.value = err;
        throw err;
      });
  }

  async function _fetchJobLocation(): Promise<
    ReadonlyDeep<openapi.JobLocationsContentOpenapi> | undefined
  > {
    if (stopLocationsController) stopLocationsController.abort();
    if (!detailedJob.value) return;
    const jobId = detailedJob.value.rockhopper_id;
    const jobDate = detailedJob.value.rockhopper_date;
    if (!jobId || !jobDate) return;
    stopLocationsController = new AbortController();
    return await fetchGetJobLocation(
      {
        job_id: jobId,
        job_date: jobDate,
      },
      {
        signal: stopLocationsController.signal,
      },
    )
      .then((data) => {
        stopLocationsError.value = undefined;
        return data;
      })
      .catch((err: unknown) => {
        if (isCancel(err)) return undefined;
        stopLocationsError.value = err;
        throw err;
      });
  }

  async function refreshDetailedJob(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    detailedJobFetchCounter.value++;
    try {
      detailedJob.value = await _fetchDetailedJob();
    } finally {
      detailedJobFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshDetailedCustomer(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    detailedCustomerFetchCounter.value++;
    try {
      detailedCustomer.value = await _fetchDetailedCustomer();
    } finally {
      detailedCustomerFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshShortZammadTickets(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    shortZammadTicketsFetchCounter.value++;
    try {
      shortZammadTickets.value = await _fetchShortZammadTickets();
    } finally {
      shortZammadTicketsFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshAirroadJobs(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    airroadJobsFetchCounter.value++;
    try {
      airroadJobs.value = await _fetchAirroadJobs();
    } finally {
      airroadJobsFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshStopImages(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    stopImagesFetchCounter.value++;
    try {
      stopImages.value = await _fetchStopImages();
    } finally {
      stopImagesFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshChannelsEmails(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    channelsEmailsFetchCounter.value++;
    try {
      channelsEmails.value = await _fetchChannelsEmails();
    } finally {
      channelsEmailsFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshStopLocations(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    stopLocationsFetchCounter.value++;
    try {
      stopLocations.value = await _fetchJobLocation();
    } finally {
      stopLocationsFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  function reload(): DetailedJobPageViewDataComposable {
    void Promise.all([
      refreshDetailedJob().then(async () => {
        // load all other related data after detailedJob successfully loaded
        return await Promise.all([
          refreshDetailedCustomer(),
          refreshShortZammadTickets(),
          refreshAirroadJobs(),
          refreshStopImages(),
          refreshStopLocations(),
        ]);
      }),
      // load separately since its not required immediately for detailedJob preview, also jobId or jobDate are not needed here
      refreshChannelsEmails(),
    ]);
    return {
      isFetchingDetailedJob,
      isFetchingDetailedCustomer,
      isFetchingShortZammadTickets,
      isFetchingAirroadJobs,
      isFetchingStopImages,
      isFetchingChannelsEmails,
      isFetchingStopLocations,

      detailedJob,
      detailedCustomer,
      shortZammadTickets,
      airroadJobs,
      stopImages,
      channelsEmails,
      stopLocations,

      detailedJobError,
      detailedCustomerError,
      shortZammadTicketsError,
      airroadJobsError,
      stopImagesError,
      channelsEmailsError,
      stopLocationsError,

      refreshDetailedJob,
      refreshDetailedCustomer,
      refreshShortZammadTickets,
      refreshAirroadJobs,
      refreshStopImages,
      refreshChannelsEmails,
      refreshStopLocations,

      reload,
    };
  }

  watch(
    [queryJobId, queryJobDate],
    async function (newValues, oldValues) {
      if (!dataRoutes.includes(route.name as string)) return;
      reload();
    },
    { immediate: true },
  );

  return {
    isFetchingDetailedJob,
    isFetchingDetailedCustomer,
    isFetchingShortZammadTickets,
    isFetchingAirroadJobs,
    isFetchingStopImages,
    isFetchingChannelsEmails,
    isFetchingStopLocations,

    detailedJob,
    detailedCustomer,
    shortZammadTickets,
    airroadJobs,
    stopImages,
    channelsEmails,
    stopLocations,

    detailedJobError,
    detailedCustomerError,
    shortZammadTicketsError,
    airroadJobsError,
    stopImagesError,
    channelsEmailsError,
    stopLocationsError,

    refreshDetailedJob,
    refreshDetailedCustomer,
    refreshShortZammadTickets,
    refreshAirroadJobs,
    refreshStopImages,
    refreshChannelsEmails,
    refreshStopLocations,

    reload,
  };
}
